/**
 * One matched list, as a compact grid cell: mono name, lead count on the
 * right, why-chips on a second line only when the rule has several
 * conditions (with one condition every cell would repeat the same chip).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Tag, Type } from 'lucide-react';

import type { ListGroupResolveRow, ListGroupRuleNode } from '../../../list-groups.types';
import { formatCompact } from '../format';

const CHIP_CLASS =
  'inline-flex items-center gap-1 rounded border border-border/60 bg-muted/40 px-1.5 py-px text-[11px] text-muted-foreground';

const WhyChip: FC<{ condition: ListGroupRuleNode }> = ({ condition }) => {
  if (condition.type === 'tag') {
    return (
      <span className={CHIP_CLASS}>
        <Tag className="h-2.5 w-2.5" /> {condition.tag}
      </span>
    );
  }
  if (condition.type === 'name') {
    return (
      <span className={CHIP_CLASS}>
        <Type className="h-2.5 w-2.5" /> “{condition.terms[0]}”
        {condition.terms.length > 1 ? ` +${condition.terms.length - 1}` : ''}
      </span>
    );
  }
  return null;
};

interface Props {
  row: ListGroupResolveRow;
  showWhy: boolean;
  showLeads: boolean;
}

export const MatchRow: FC<Props> = ({ row, showWhy, showLeads }) => {
  const name = row.name ?? `#${row.wiserId}`;
  return (
    <li className="flex flex-col justify-center gap-1 rounded-md border border-border/50 bg-muted/20 px-2.5 py-1.5 transition-colors hover:border-border hover:bg-muted/40">
      <span className="flex items-baseline justify-between gap-2">
        <span className="truncate font-mono text-xs" title={name}>
          {name}
        </span>
        {showLeads && row.leadCount != null ? (
          <span
            className="shrink-0 font-mono text-[11px] tabular-nums text-muted-foreground"
            title={`${row.leadCount.toLocaleString('en-US')} leads`}
          >
            {formatCompact(row.leadCount)}
          </span>
        ) : null}
      </span>
      {showWhy && (row.matchedBy ?? []).length > 0 ? (
        <span className="flex flex-wrap items-center gap-1">
          {(row.matchedBy ?? []).map((condition, i) => (
            <WhyChip key={i} condition={condition} />
          ))}
        </span>
      ) : null}
    </li>
  );
};
